import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import {
  CALENDAR_TOOLBAR_VIEWPORT_PADDING_PX,
  computeCalendarToolbarPosition,
  type CalendarToolbarPosition,
} from "../../utils/calendarToolbarPosition";

interface CalendarAnchoredPopoverProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "style"
> {
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  /** Horizontal gap between the anchor and the popover, in px */
  offset?: number;
}

/** Keeps a pointer arrow off the popover's rounded corners. */
const ANCHOR_Y_INSET_PX = 14;

/**
 * A viewport-positioned sidecar anchored beside a calendar element (a block
 * or travel pin). Rendering through a portal lets it escape the day column's
 * clipping and overlap layers; it prefers the anchor's right side and flips
 * left near the viewport edge. Pointer and click events stop here so they
 * never reach the column's drag-create or dnd-kit listeners.
 *
 * The popover exposes `data-side` and a `--popover-anchor-y` custom property
 * (the anchor's vertical centre relative to the popover) so content can draw
 * an arrow pointing back at its anchor.
 */
export function CalendarAnchoredPopover({
  anchorRef,
  children,
  className,
  offset = 0,
  onPointerDown,
  onClick,
  ...rest
}: CalendarAnchoredPopoverProps) {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<
    (CalendarToolbarPosition & { anchorY: number }) | null
  >(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const toolbar = toolbarRef.current;
    if (!anchor || !toolbar) return;

    const anchorRect = anchor.getBoundingClientRect();
    const padding = CALENDAR_TOOLBAR_VIEWPORT_PADDING_PX;
    const outsideViewport =
      anchorRect.bottom <= padding ||
      anchorRect.top >= window.innerHeight - padding ||
      anchorRect.right <= padding ||
      anchorRect.left >= window.innerWidth - padding;

    if (outsideViewport) {
      setPosition(null);
      return;
    }

    const toolbarRect = toolbar.getBoundingClientRect();
    const placement = computeCalendarToolbarPosition(
      anchorRect,
      { width: toolbarRect.width, height: toolbarRect.height },
      { width: window.innerWidth, height: window.innerHeight },
      padding,
      offset,
    );
    const anchorY = Math.min(
      Math.max(
        anchorRect.top + anchorRect.height / 2 - placement.top,
        ANCHOR_Y_INSET_PX,
      ),
      Math.max(toolbarRect.height - ANCHOR_Y_INSET_PX, ANCHOR_Y_INSET_PX),
    );
    const nextPosition = { ...placement, anchorY };

    setPosition((current) =>
      current?.top === nextPosition.top &&
      current.left === nextPosition.left &&
      current.side === nextPosition.side &&
      current.anchorY === nextPosition.anchorY
        ? current
        : nextPosition,
    );
  }, [anchorRef, offset]);

  useLayoutEffect(() => {
    updatePosition();

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updatePosition);
    if (anchorRef.current) resizeObserver?.observe(anchorRef.current);
    if (toolbarRef.current) resizeObserver?.observe(toolbarRef.current);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      resizeObserver?.disconnect();
    };
  }, [anchorRef, updatePosition]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      {...rest}
      ref={toolbarRef}
      className={clsx("fixed z-[60]", className)}
      style={
        {
          top: position?.top ?? 0,
          left: position?.left ?? 0,
          visibility: position ? "visible" : "hidden",
          "--popover-anchor-y": `${position?.anchorY ?? 0}px`,
        } as CSSProperties
      }
      data-calendar-transient-control
      data-side={position?.side}
      onPointerDown={(event) => {
        event.stopPropagation();
        onPointerDown?.(event);
      }}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

interface CalendarActionToolbarProps {
  anchorRef: RefObject<HTMLElement | null>;
  ariaLabel: string;
  children: ReactNode;
  onMouseEnter?: MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
}

/** The hover/tap action sidecar for calendar blocks. */
export function CalendarActionToolbar({
  anchorRef,
  ariaLabel,
  children,
  onMouseEnter,
  onMouseLeave,
}: CalendarActionToolbarProps) {
  return (
    <CalendarAnchoredPopover
      anchorRef={anchorRef}
      role="toolbar"
      aria-label={ariaLabel}
      className="inline-flex flex-col gap-0.5 rounded-lg border border-border bg-surface p-0.5 shadow-md"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </CalendarAnchoredPopover>
  );
}
